import { useState } from 'react';
import {
  Table,
  Text,
  Group,
  Badge,
  ActionIcon,
  Alert,
  Card,
  Tooltip,
  Stack,
  ScrollArea,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconX,
  IconKey,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { showSuccess } from '../../lib/notifications';
import { serviceAccessApi } from '../../lib/api';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { PageTransition } from '../../components/PageTransition';

export function AdminAccessRequestsPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  const { data: accessRequests, isLoading } = useQuery({
    queryKey: ['accessRequests'],
    queryFn: async () => {
      try {
        const response = await serviceAccessApi.listRequests();
        return response.data;
      } catch (error) {
        console.error('Failed to fetch access requests:', error);
        throw error;
      }
    },
  });

  const respondToAccessRequest = useMutation({
    mutationFn: async ({ requestId, status }: { requestId: string; status: 'APPROVED' | 'REJECTED' }) => {
      const response = await serviceAccessApi.respondToRequest(requestId, status);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accessRequests']);
      showSuccess('Access request updated successfully');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to update access request');
    },
  });

  if (isLoading) {
    return <LoadingOverlay message="Loading..." />;
  }

  return (
    <PageTransition>
        <PageHeader
          title="Environment Access Requests"
          description="Manage environment variable access requests from users"
        />

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            mb="md"
            title="Error"
            onClose={() => setError('')}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        <Card mt="md" shadow="sm" radius="md" withBorder>
          <ScrollArea>
          <Table>
            <thead>
              <tr>
                <th>User</th>
                <th>Service & Environment</th>
                <th>Access Type</th>
                <th>Reason</th>
                <th>Requested At</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {accessRequests?.map((request: any) => (
                <tr key={request.id}>
                  <td>
                    <Text>
                      {request.user.firstName} {request.user.lastName}
                    </Text>
                    <Text size="xs" color="dimmed">
                      {request.user.email}
                    </Text>
                  </td>
                  <td>
                    <Text>{request.environment.service.name}</Text>
                    <Text size="xs" color="dimmed">
                      Environment: {request.environment.name}
                    </Text>
                  </td>
                  <td>
                    <Badge color={request.accessType === 'WRITE' ? 'red' : 'blue'}>
                      {request.accessType}
                    </Badge>
                  </td>
                  <td>
                    <Text size="sm">
                      {request.reason || 'No reason provided'}
                    </Text>
                  </td>
                  <td>
                    <Text size="sm" color="dimmed">
                      {new Date(request.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Text>
                  </td>
                  <td>
                    <Badge
                      color={
                        request.status === 'PENDING'
                          ? 'yellow'
                          : request.status === 'APPROVED'
                          ? 'green'
                          : 'red'
                      }
                    >
                      {request.status}
                    </Badge>
                  </td>
                  <td>
                    {request.status === 'PENDING' && (
                      <Group spacing={4}>
                        <Tooltip label="Approve">
                          <ActionIcon
                            color="green"
                            onClick={() =>
                              respondToAccessRequest.mutate({
                                requestId: request.id,
                                status: 'APPROVED',
                              })
                            }
                            loading={respondToAccessRequest.isLoading}
                          >
                            <IconCheck size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Reject">
                          <ActionIcon
                            color="red"
                            onClick={() =>
                              respondToAccessRequest.mutate({
                                requestId: request.id,
                                status: 'REJECTED',
                              })
                            }
                            loading={respondToAccessRequest.isLoading}
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          </ScrollArea>

          {(!accessRequests || accessRequests.length === 0) && (
            <Stack align="center" py="xl">
              <IconKey size={48} color="gray" />
              <Text color="dimmed" size="lg">
                No access requests found
              </Text>
              <Text color="dimmed" size="sm">
                Users will appear here when they request access to environment variables
              </Text>
            </Stack>
          )}
        </Card>
    </PageTransition>
  );
}
